import type { QuestObjective } from "@roll1/shared";
import { Check } from "lucide-react";
import { useI18n } from "../../i18n";
import { useQuestObjectiveTicks } from "../../contexts/QuestObjectivesContext";
import { useQuestObjectiveNotes } from "../../contexts/QuestObjectiveNotesContext";
import { useState } from "react";
import { ObjectiveGameHint } from "../studio/remarks/ObjectiveGameHint";
import { InlineNoteForm } from "../studio/InlineNoteForm";

interface QuestObjectiveListProps {
  /** The quest's entry id — the tick addresses its objective by (entry, objective id). */
  entryId: string;
  /** As the viewer has them; a player's copy has no hidden objectives (the server strips them). */
  objectives: readonly QuestObjective[] | undefined;
  isGM: boolean;
  /** Smaller type for the beat's quest cards and «План игры». */
  compact?: boolean;
  /** Studio card (mockup): objectives as rows with dividers, the title bold. */
  divided?: boolean;
  /** Shown, not given: no box to tick (a beat that is only looked at). */
  readOnly?: boolean;
  /** At the table: a hint from the game stands under its objective (group E, AU-16). */
  gameHint?: boolean;
}

/**
 * A quest's objectives — title, description, done or not (GM Studio quests 1).
 * For the GM each objective is ticked in one click, here, without editing the
 * entry (journal:objective-set through QuestObjectivesContext); the box shows
 * the server's value and «saving» until the answer. A player sees ✓ / ○ only.
 */
export default function QuestObjectiveList({ entryId, objectives, isGM, compact = false, divided = false, readOnly = false, gameHint = false }: QuestObjectiveListProps) {
  const { t } = useI18n();
  const ticks = useQuestObjectiveTicks();
  // Studio only: «＋ заметка к цели» (P03).
  const notesApi = useQuestObjectiveNotes();
  const notes = isGM ? notesApi : null;
  // GK-1: the objective whose «＋ заметка к цели» form is open.
  const [noteFormFor, setNoteFormFor] = useState<string | null>(null);
  const visible = isGM ? (objectives ?? []) : (objectives ?? []).filter((o) => !o.hidden);
  if (visible.length === 0) return null;
  const canTick = isGM && ticks !== null && !readOnly;

  return (
    <ul className={divided ? "divide-y divide-line" : `mt-1 ${compact ? "space-y-1" : "space-y-1.5"}`} data-testid="quest-objectives" data-entry-id={entryId}>
      {visible.map((o, i) => {
        const pending = ticks?.pending(entryId, o.id) ?? false;
        const label = o.text.trim() !== "" ? o.text : t("journal.form.quest.objectiveText");
        return (
          <li
            key={o.id ?? i}
            className={`grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-2 ${divided ? "px-3 py-2.5" : ""}`}
            data-testid="quest-objective"
            data-objective-id={o.id}
            data-completed={o.completed ? "true" : "false"}
          >
            {canTick ? (
              <button
                type="button"
                role="checkbox"
                aria-checked={o.completed}
                aria-busy={pending}
                aria-label={label}
                title={pending ? t("journal.objective.saving") : label}
                disabled={pending}
                data-testid="quest-objective-tick"
                className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-sm border transition-colors disabled:cursor-wait disabled:opacity-60 ${
                  o.completed
                    ? "border-ok bg-ok text-ok-ink"
                    : "border-dim hover:border-ok"
                }`}
                onClick={() => ticks?.set(entryId, o.id, !o.completed)}
              >
                {o.completed ? <Check size={12} strokeWidth={3} aria-hidden="true" /> : null}
              </button>
            ) : (
              <span className={`text-ui ${o.completed ? "text-ok" : "text-dim"}`} aria-hidden="true">
                {o.completed ? "✓" : "○"}
              </span>
            )}
            <span
              className={`min-w-0 break-words ${divided ? "text-ui font-semibold" : compact ? "text-meta" : "text-ui"} ${
                o.completed ? "text-dim line-through" : "text-fg"
              }`}
              data-testid="quest-objective-text"
            >
              {o.text}
              {isGM && o.hidden ? (
                <span className="ml-1.5 rounded border border-warn/50 px-1 text-label text-warn no-underline">
                  {t("journal.form.quest.objectiveHidden")}
                </span>
              ) : null}
              {pending ? <span className="ml-1.5 text-label text-dim">{t("journal.objective.saving")}</span> : null}
            </span>
            {o.description !== undefined && o.description.trim() !== "" ? (
              <p
                className={`col-start-2 whitespace-pre-wrap break-words text-muted-foreground ${compact ? "text-pill" : "text-meta"}`}
                data-testid="quest-objective-description"
              >
                {o.description}
              </p>
            ) : null}
            {notes !== null && o.id !== undefined && notes.renderNotes !== undefined ? (
              <div className="col-start-2 flex flex-col gap-1.5" data-testid="quest-objective-notes">
                {notes.renderNotes(entryId, o.id)}
              </div>
            ) : null}
            {notes !== null && o.id !== undefined && noteFormFor === o.id ? (
              <div className="col-start-2">
                <InlineNoteForm
                  t={t}
                  onCancel={() => setNoteFormFor(null)}
                  onSubmit={(draft) => {
                    setNoteFormFor(null);
                    notes.addNote(entryId, o.id as string, draft);
                  }}
                />
              </div>
            ) : notes !== null && o.id !== undefined ? (
              <button
                type="button"
                className="col-start-2 justify-self-start rounded px-1 text-pill text-hl hover:bg-hl/10"
                data-testid="quest-objective-add-note"
                onClick={() => setNoteFormFor(o.id ?? null)}
              >
                {t("studio.owner.noteToObjective")}
              </button>
            ) : null}
            {gameHint && isGM && !readOnly && o.id !== undefined ? <ObjectiveGameHint questId={entryId} objectiveId={o.id} /> : null}
          </li>
        );
      })}
    </ul>
  );
}
